import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
// Import AdminAuthService/RouterUrlsEnum/performLogout/ThemeService/BrandLogoComponent
// from their source modules, not the '@bodyanalytics/ui-core' barrel — this component
// is itself re-exported by that barrel, so a barrel import would create a circular
// import-alias that breaks Jest compilation for consumers (same rule as admin-role.guard.ts).
import { AdminAuthService } from '../../services/auth/admin-auth.service';
import { performLogout } from '../../helper/session';
import { RouterUrlsEnum } from '../../enums/router-urls.enum';
import { ThemeService } from '../../theme/theme.service';
import { BrandLogoComponent } from '../../shared-components/brand-logo/brand-logo.component';

/**
 * Standalone "Access Denied" splash (#1553 Scope A).
 *
 * Reached by `AdminRoleGuard` for two distinct reasons, both redirecting here:
 *  - Role denial (authenticated but not ADMIN/OPERATOR) — a real access
 *    decision. No `?reason` query param.
 *  - Verification failure (identity lookup 5xx / token error — the guard's
 *    'error' outcome or its fail-closed catch) — can dead-end a LEGITIMATE
 *    admin on a transient failure. Tagged `?reason=error`, which renders an
 *    HONEST error state ("We couldn't load your account" + "Try again")
 *    instead of the Access Denied copy (#2070) — a role-denied user retrying
 *    gains nothing, so only this path gets the retry.
 *
 * Rendered without any admin-shell chrome — the host app (`apps/front/admin`)
 * gates that at the `AppComponent` level (mirrors the customer app's
 * `isPortalRoute()` pattern), not here. This component is shared (also used by
 * the customer app's `/access-denied` route), so it must not assume any
 * app-specific chrome/services beyond the root-provided ones used below.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-access-denied',
  standalone: true,
  templateUrl: './access-denied.component.html',
  styleUrls: ['./access-denied.component.scss'],
  imports: [EmptyStateComponent, BrandLogoComponent],
})
export class AccessDeniedComponent {
  private readonly auth = inject(AuthService);
  private readonly adminAuth = inject(AdminAuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly theme = inject(ThemeService);

  private readonly queryParamMap = toSignal(this.route.queryParamMap, { initialValue: null });

  /** True only for the guard's fail-closed catch path — see class doc. */
  protected readonly isRetryableError = computed(() => this.queryParamMap()?.get('reason') === 'error');

  /** Primary CTA — always calls the one safe shared logout path. */
  protected logout(): void {
    performLogout(this.auth, this.adminAuth);
  }

  /**
   * Secondary CTA. `prompt: 'login'` forces Auth0 to show a fresh credential
   * prompt instead of silently reusing the shared cross-app SSO session — the
   * whole reason a non-admin can land here without ever seeing a login screen.
   */
  protected signInDifferentAccount(): void {
    this.auth.loginWithRedirect({ authorizationParams: { prompt: 'login' } });
  }

  /**
   * Fail-closed-error-only retry: re-enter a guarded route to re-evaluate the
   * guard. Hardcodes the admin dashboard because `?reason=error` is only ever
   * produced by `AdminRoleGuard` (admin app) — the customer app's own
   * `/access-denied` route never carries it, so this branch is unreachable
   * (and the button never renders) there. Revisit if this splash gains a
   * second fail-closed-error producer outside the admin app.
   */
  protected retry(): void {
    this.router.navigateByUrl(RouterUrlsEnum.AUTH_ADMIN_DASHBOARD);
  }
}
